import { cur } from '@/lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Layers, RefreshCw, ChevronDown, ChevronRight, Loader2, Settings2, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';
import SmartReconcileWizard from '@/components/banking/SmartReconcileWizard';
import BankCodeDictionaryDialog from '@/components/banking/BankCodeDictionaryDialog';

/**
 * iter437 — Group & Bulk Link page.
 *
 * Surfaces unlinked bank statement transactions grouped by smart payee key
 * so the user can categorise/assign N matching lines in one shot. Drops
 * groups with fewer than `minCount` lines (default 3).
 *
 * iter438 — Per-row customise mode: each row in a group can be routed
 * to a different supplier/customer/employee/expense category/etc., or
 * skipped entirely. Plus an opt-in "Also create a Banking Rule" so
 * future matching lines auto-link.
 */
export default function BankingGroupLinkPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(365);
  const [minCount, setMinCount] = useState(3);
  const [direction, setDirection] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [linkGroup, setLinkGroup] = useState(null); // group being linked
  const [wizardOpen, setWizardOpen] = useState(false);
  // iter444 — bank-code dictionary editor opened from decoded-label badges
  const [dictState, setDictState] = useState({ open: false, code: '', label: '' });
  const openDict = (code = '', label = '') => setDictState({ open: true, code, label });

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const r = await api.get('/banking/grouped-unlinked', {
        params: { days, min_count: minCount, direction },
      });
      setGroups(r.data?.groups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, minCount, direction]);

  const totalLines = groups.reduce((s, g) => s + g.count, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="banking-group-link-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">
              <Layers size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight">Group &amp; Bulk Link</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Bulk-categorise unlinked bank lines. Pick a target for all rows, or use "Customise per-row" to route each line independently.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchGroups} disabled={loading} data-testid="group-link-refresh">
              <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm"
              onClick={() => setWizardOpen(true)}
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white shadow-md"
              data-testid="open-smart-reconcile-btn">
              <Sparkles size={12} className="mr-1" /> Smart Reconcile
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3">
                <label className="text-xs text-muted-foreground mb-1 block">Look back</label>
                <Select value={String(days)} onValueChange={v => setDays(parseInt(v, 10))}>
                  <SelectTrigger data-testid="gl-days"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="90">3 months</SelectItem>
                    <SelectItem value="180">6 months</SelectItem>
                    <SelectItem value="365">12 months</SelectItem>
                    <SelectItem value="730">24 months</SelectItem>
                    <SelectItem value="1095">3 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs text-muted-foreground mb-1 block">Minimum lines</label>
                <Select value={String(minCount)} onValueChange={v => setMinCount(parseInt(v, 10))}>
                  <SelectTrigger data-testid="gl-min"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 or more</SelectItem>
                    <SelectItem value="3">3 or more (recommended)</SelectItem>
                    <SelectItem value="5">5 or more</SelectItem>
                    <SelectItem value="10">10 or more</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs text-muted-foreground mb-1 block">Direction</label>
                <Select value={direction} onValueChange={setDirection}>
                  <SelectTrigger data-testid="gl-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="outgoing">Outgoing (payments)</SelectItem>
                    <SelectItem value="incoming">Incoming (receipts)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 grid grid-cols-2 gap-2">
                <div className="rounded-md bg-info/10 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-info">Groups</div>
                  <div className="text-lg font-bold text-info">{groups.length}</div>
                </div>
                <div className="rounded-md bg-amber-100 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800">Unlinked lines</div>
                  <div className="text-lg font-bold text-amber-900">{totalLines}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center"><Loader2 className="animate-spin inline" size={20} /></div>
            ) : groups.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <div className="text-sm font-medium">No grouped lines</div>
                <div className="text-xs mt-1">Try a wider time window or lower the minimum-lines threshold.</div>
              </div>
            ) : (
              <div className="divide-y">
                {groups.map(g => (
                  <GroupRow key={g.group_id}
                    group={g}
                    expanded={!!expanded[g.group_id]}
                    onToggle={() => setExpanded(e => ({ ...e, [g.group_id]: !e[g.group_id] }))}
                    onLink={() => setLinkGroup(g)}
                    onOpenDict={openDict}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {linkGroup && (
        <BulkLinkDialog
          group={linkGroup}
          onClose={(refresh) => { setLinkGroup(null); if (refresh) fetchGroups(); }}
          onOpenDict={openDict}
        />
      )}

      <SmartReconcileWizard
        open={wizardOpen}
        onClose={(refresh) => { setWizardOpen(false); if (refresh) fetchGroups(); }}
        onApplied={() => fetchGroups()}
      />

      <BankCodeDictionaryDialog
        open={dictState.open}
        defaultNewCode={dictState.code}
        defaultNewLabel={dictState.label}
        onClose={() => setDictState({ open: false, code: '', label: '' })}
        onDirty={() => fetchGroups()}
      />
    </DashboardLayout>
  );
}

function GroupRow({ group, expanded, onToggle, onLink, onOpenDict }) {
  const dirColor = group.direction === 'incoming' ? 'text-success' : 'text-error';
  return (
    <div data-testid={`group-row-${group.group_id}`}>
      <div role="button" tabIndex={0} onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left cursor-pointer">
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Badge variant="outline" className="text-[10px] capitalize">{group.key_type}</Badge>
        <span className="flex-1 font-medium truncate">{group.display_label}</span>
        <span className="text-xs text-muted-foreground hidden sm:inline">{group.first_date} → {group.last_date}</span>
        <Badge className="bg-amber-100 text-amber-800 border-amber-300">{group.count} lines</Badge>
        <span className={`font-bold ${dirColor}`}>{cur()} {group.total_amount.toLocaleString()}</span>
        <Button size="sm" onClick={(e) => { e.stopPropagation(); onLink(); }}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid={`link-group-${group.group_id}`}>
          Link / Categorise
        </Button>
      </div>
      {expanded && (
        <div className="bg-muted/20 px-12 pb-3 border-t">
          <div className="overflow-x-auto">
            <table className="w-full text-xs mt-2">
              <thead className="text-muted-foreground">
                <tr><th className="text-left py-1">Date</th><th className="text-right">Amount</th><th className="text-left pl-3">Description</th></tr>
              </thead>
              <tbody>
                {group.transactions.map((t, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="py-1.5 font-mono text-muted-foreground">{t.date}</td>
                    <td className="py-1.5 text-right font-medium">{cur()} {t.amount.toLocaleString()}</td>
                    <td className="py-1.5 pl-3 max-w-md">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <DecodedLabelBadge txn={t} onOpenDict={onOpenDict} testIdSuffix={`${group.group_id}-${i}`} />
                        <span className="truncate" title={t.description}>{t.description}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * iter444 — Small clickable badge that surfaces `decoded_label`.
 *
 * Hover shows a tooltip explaining the auto-decode; click opens the
 * Bank-Code Dictionary editor pre-filled with the matched code so
 * the user can adjust the friendly label without leaving the screen.
 */
function DecodedLabelBadge({ txn, onOpenDict, testIdSuffix = '' }) {
  if (!txn?.decoded_label) return null;
  const desc = txn.description || '';
  // Best-effort: surface the upper-case "code token" from the description
  // so the dictionary editor lands pre-filled with the right key. Falls
  // back to the decoded label itself if no obvious token is present.
  const tokenMatch = desc.match(/[A-Z][A-Z0-9_\-/]{2,}/);
  const guessedCode = (tokenMatch ? tokenMatch[0] : txn.decoded_label).toUpperCase();
  const handleClick = (e) => {
    e.stopPropagation();
    onOpenDict?.(guessedCode, txn.decoded_label);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      title={`Auto-decoded from "${desc.slice(0, 80)}${desc.length > 80 ? '…' : ''}" — click to edit the dictionary`}
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-200 hover:border-sky-300 transition-colors whitespace-nowrap cursor-pointer"
      data-testid={`decoded-badge-${testIdSuffix}`}>
      {txn.decoded_label}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Bulk link dialog with single-picker and per-row modes
// ───────────────────────────────────────────────────────────────────────────

const PAYEE_TYPE_OPTIONS_OUT = [
  { v: 'expense_category', l: 'Expense (no supplier)' },
  { v: 'supplier', l: 'Supplier' },
  { v: 'employee', l: 'Employee' },
  { v: 'partner', l: 'Partner withdrawal' },
];
const PAYEE_TYPE_OPTIONS_IN = [
  { v: 'customer', l: 'Customer (receipt)' },
  { v: 'platform', l: 'Delivery Platform' },
  { v: 'partner', l: 'Partner contribution' },
];
const EXPENSE_CATEGORIES = [
  'Rent', 'Utilities', 'Maintenance', 'Cleaning', 'Salary', 'Travel', 'Fuel', 'Transport',
  'Bank Fees', 'VAT Settlement', 'Government', 'Subscription Fees', 'Marketing', 'Other',
];

function BulkLinkDialog({ group, onClose, onOpenDict }) {
  const incoming = group.direction === 'incoming';
  const payeeOptions = incoming ? PAYEE_TYPE_OPTIONS_IN : PAYEE_TYPE_OPTIONS_OUT;

  // Mode: 'single' (one picker for all) or 'per_row' (per-line targets)
  const [mode, setMode] = useState('single');

  // Single-picker state
  const [payeeType, setPayeeType] = useState(payeeOptions[0].v);
  const [payeeId, setPayeeId] = useState('');
  const [category, setCategory] = useState('Other');
  const [subCategory, setSubCategory] = useState('');
  const [createRule, setCreateRule] = useState(false);
  // iter439 — When the group has a dominant bank account, the user can
  // optionally scope the auto-rule to that bank only (default: off →
  // rule applies to every bank in the tenant).
  const [scopeRuleToBank, setScopeRuleToBank] = useState(false);

  // Per-row state: rowAssignments[txnIndexKey] = { payee_type, payee_id, category, sub_category, skip }
  const [rowAssignments, setRowAssignments] = useState({});
  const txnKey = (t) => `${t.statement_id}::${t.txn_index}`;

  const [busy, setBusy] = useState(false);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="bulk-link-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'single' ? `Link ${group.count} ${incoming ? 'incoming' : 'outgoing'} lines` : `Customise ${group.count} lines`}
            <span className="ml-2 font-normal text-sm text-muted-foreground">· {cur()} {group.total_amount.toLocaleString()}</span>
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{group.display_label} · {group.first_date} → {group.last_date}</p>
        </DialogHeader>

        {mode === 'single' ? (
          <SinglePickerMode
            incoming={incoming}
            payeeOptions={payeeOptions}
            payeeType={payeeType} setPayeeType={setPayeeType}
            payeeId={payeeId} setPayeeId={setPayeeId}
            category={category} setCategory={setCategory}
            subCategory={subCategory} setSubCategory={setSubCategory}
            createRule={createRule} setCreateRule={setCreateRule}
            scopeRuleToBank={scopeRuleToBank} setScopeRuleToBank={setScopeRuleToBank}
            group={group}
          />
        ) : (
          <PerRowMode
            incoming={incoming}
            payeeOptions={payeeOptions}
            group={group}
            rowAssignments={rowAssignments}
            setRowAssignments={setRowAssignments}
            txnKey={txnKey}
            onOpenDict={onOpenDict}
          />
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <button type="button"
            onClick={() => setMode(mode === 'single' ? 'per_row' : 'single')}
            className="text-xs text-info hover:underline inline-flex items-center gap-1"
            data-testid="customize-per-row-btn">
            <Settings2 size={11} />
            {mode === 'single' ? 'Customise per-row' : '← Back to single picker'}
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onClose()} disabled={busy}>Cancel</Button>
            <Button
              onClick={async () => {
                setBusy(true);
                try {
                  if (mode === 'single') {
                    await submitSingle({ group, payeeType, payeeId, category, subCategory, createRule, scopeRuleToBank });
                  } else {
                    await submitPerRow({ group, rowAssignments, txnKey });
                  }
                  onClose(true);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message || 'Failed');
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="bulk-link-submit">
              {busy ? <><Loader2 size={12} className="animate-spin mr-1" /> Linking…</> :
                mode === 'single' ? `Link all ${group.count}` : `Apply per-row`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SinglePickerMode({
  incoming, payeeOptions, payeeType, setPayeeType, payeeId, setPayeeId,
  category, setCategory, subCategory, setSubCategory, createRule, setCreateRule,
  scopeRuleToBank, setScopeRuleToBank, group,
}) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    if (payeeType === 'expense_category') return;
    const ep = { supplier: '/suppliers', customer: '/customers', employee: '/employees', partner: '/partners', platform: '/delivery-platforms' }[payeeType];
    if (!ep) return;
    api.get(ep).then(r => setOptions(r.data || [])).catch(() => setOptions([]));
  }, [payeeType]);

  return (
    <div className="space-y-3" data-testid="single-picker-mode">
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Link all as</label>
        <Select value={payeeType} onValueChange={(v) => { setPayeeType(v); setPayeeId(''); }}>
          <SelectTrigger data-testid="bulk-payee-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {payeeOptions.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {payeeType === 'expense_category' ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="bulk-category"><SelectValue /></SelectTrigger>
              <SelectContent>{EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Sub-category (optional)</label>
            <Input value={subCategory} onChange={e => setSubCategory(e.target.value)}
              placeholder="e.g. Monthly VAT Payment" data-testid="bulk-subcategory" />
          </div>
        </div>
      ) : (
        <div data-testid="bulk-payee-id">
          <label className="text-xs text-muted-foreground mb-1 block">Pick {payeeType}</label>
          <SearchableSelect value={payeeId} onChange={setPayeeId}
            options={options.map(o => ({ value: o.id, label: o.name || o.full_name || o.id }))}
            placeholder={`Choose ${payeeType}…`} />
        </div>
      )}

      {/* iter438 — opt-in: auto-create a Banking Rule so future imports auto-link */}
      <label className="flex items-start gap-2 p-2.5 rounded-md bg-info/5 border border-info/20 cursor-pointer hover:bg-info/10 transition-colors">
        <input type="checkbox" checked={createRule} onChange={e => setCreateRule(e.target.checked)}
          className="mt-0.5" data-testid="create-rule-checkbox" />
        <div className="text-xs">
          <div className="font-medium text-foreground">Also create a Banking Rule for future imports</div>
          <div className="text-muted-foreground mt-0.5">
            Rule key: <span className="font-mono">{group.key_type} · {group.display_label}</span>. Any future statement line matching this signature will be linked automatically.
          </div>
        </div>
      </label>

      {/* iter439 — Optional bank-account scope. Only useful when the group
          maps cleanly to one bank account (otherwise the rule would
          ignore the other banks' lines silently). */}
      {createRule && group.bank_account_id && (
        <label className="flex items-start gap-2 p-2.5 ml-6 rounded-md bg-muted/30 border border-border cursor-pointer hover:bg-muted/50 transition-colors"
          data-testid="scope-rule-bank-row">
          <input type="checkbox" checked={scopeRuleToBank} onChange={e => setScopeRuleToBank(e.target.checked)}
            className="mt-0.5" data-testid="scope-rule-bank-checkbox" />
          <div className="text-xs">
            <div className="font-medium text-foreground">Limit rule to this bank only</div>
            <div className="text-muted-foreground mt-0.5">
              {group.bank_name || 'this bank account'}
              {group.bank_account_count > 1 && (
                <span className="text-amber-700 ml-1">· this group spans {group.bank_account_count} bank accounts — scoping uses the dominant one only</span>
              )}
              {!scopeRuleToBank && <span> · by default the rule applies to every bank in the tenant.</span>}
            </div>
          </div>
        </label>
      )}
    </div>
  );
}

const PER_ROW_MAX = 200;

function PerRowMode({ incoming, payeeOptions, group, rowAssignments, setRowAssignments, txnKey, onOpenDict }) {
  const assigned = group.transactions.filter(t => rowAssignments[txnKey(t)] && !rowAssignments[txnKey(t)].skip).length;
  const skipped = group.transactions.filter(t => rowAssignments[txnKey(t)]?.skip).length;
  // Cap rendered rows to avoid ResizeObserver overlay / layout thrash on
  // very large groups (the tenant has groups of 1300+ lines). Beyond the
  // cap we show a banner suggesting single-picker mode for the bulk and
  // per-row for the remainder.
  const overflow = group.transactions.length > PER_ROW_MAX;
  const visible = overflow ? group.transactions.slice(0, PER_ROW_MAX) : group.transactions;

  return (
    <div className="space-y-3" data-testid="per-row-mode">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{assigned} assigned · {skipped} skipped · {group.count - assigned - skipped} unset</span>
      </div>
      {overflow && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="per-row-overflow-banner">
          Showing the first {PER_ROW_MAX} of {group.transactions.length} rows. For the remainder, use single-picker mode after applying these.
        </div>
      )}
      <div className="border rounded-md max-h-96 overflow-y-auto divide-y divide-border">
        {visible.map(t => {
          const key = txnKey(t);
          const cur = rowAssignments[key] || {};
          const update = (patch) => setRowAssignments(s => ({ ...s, [key]: { ...s[key], ...patch } }));
          return (
            <div key={key} className={`p-2.5 ${cur.skip ? 'opacity-50 bg-muted/30' : ''}`} data-testid={`per-row-${key}`}>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-mono text-muted-foreground">{t.date}</span>
                <span className="font-bold">{cur()} {t.amount.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-1.5 mb-2 min-w-0">
                <DecodedLabelBadge txn={t} onOpenDict={onOpenDict} testIdSuffix={`row-${key}`} />
                <div className="text-xs truncate text-muted-foreground flex-1" title={t.description}>{t.description}</div>
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4">
                  <Select value={cur.payee_type || ''} onValueChange={(v) => update({ payee_type: v, payee_id: '', skip: false })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Link as…" /></SelectTrigger>
                    <SelectContent>
                      {payeeOptions.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-7">
                  {cur.payee_type === 'expense_category' ? (
                    <div className="flex gap-1">
                      <Select value={cur.category || 'Other'} onValueChange={v => update({ category: v })}>
                        <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={cur.sub_category || ''} onChange={e => update({ sub_category: e.target.value })}
                        placeholder="Sub-cat" className="h-8 text-xs flex-1" />
                    </div>
                  ) : cur.payee_type ? (
                    <PerRowPayeePicker payeeType={cur.payee_type} value={cur.payee_id || ''} onChange={v => update({ payee_id: v })} />
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Pick a type first</span>
                  )}
                </div>
                <div className="col-span-1 text-right">
                  <button type="button" onClick={() => update({ skip: !cur.skip })}
                    className={`text-[10px] underline hover:no-underline ${cur.skip ? 'text-foreground' : 'text-muted-foreground'}`}
                    data-testid={`per-row-skip-${key}`}>
                    {cur.skip ? 'undo' : 'skip'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PerRowPayeePicker({ payeeType, value, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    const ep = { supplier: '/suppliers', customer: '/customers', employee: '/employees', partner: '/partners', platform: '/delivery-platforms' }[payeeType];
    if (!ep) return;
    api.get(ep).then(r => setOpts(r.data || [])).catch(() => setOpts([]));
  }, [payeeType]);
  return (
    <SearchableSelect value={value} onChange={onChange}
      options={opts.map(o => ({ value: o.id, label: o.name || o.full_name || o.id }))}
      placeholder={`Pick ${payeeType}…`} className="h-8" />
  );
}

// ───────────── submit helpers ─────────────

// Map UI `payee_type` to a backend BankingRule `kind` from ALLOWED_RULE_KINDS.
const PAYEE_TYPE_TO_RULE_KIND = {
  expense_category: 'expense',
  supplier: 'supplier_payment',
  customer: 'customer_receipt',
  employee: 'employee_payment',
  partner: 'partner_payment',
  platform: 'platform_settlement',
};

function buildRulePayload({ group, payeeType, payeeId, category, subCategory, scopeRuleToBank }) {
  const kind = PAYEE_TYPE_TO_RULE_KIND[payeeType] || 'expense';
  // BankingRule `_rule_matches` only matches against description text, so a
  // 'contains' rule keyed on `group.key_value` is the right shape (works for
  // account-number, normalised name, and signature group keys alike).
  const ruleName = `Group-link: ${group.display_label}${subCategory ? ` (${subCategory})` : ''}`;
  return {
    name: ruleName.slice(0, 80),
    match_type: 'contains',
    match_value: group.key_value,
    direction: group.direction === 'incoming' ? 'incoming' : 'outgoing',
    kind,
    category: payeeType === 'expense_category' ? (category || 'Other') : null,
    supplier_id: payeeType === 'supplier' ? payeeId : null,
    payee_id: payeeType !== 'expense_category' ? payeeId : null,
    // iter439 — Optional bank-account scope (default null → applies to
    // all banks in the tenant).
    bank_account_id: scopeRuleToBank && group.bank_account_id ? group.bank_account_id : null,
    priority: 50,
    enabled: true,
  };
}

async function submitSingle({ group, payeeType, payeeId, category, subCategory, createRule, scopeRuleToBank }) {
  if (payeeType !== 'expense_category' && !payeeId) throw new Error('Select a payee first');
  const txn_refs = group.transactions.map(t => ({ statement_id: t.statement_id, txn_index: t.txn_index }));
  const body = {
    txn_refs,
    payee_type: payeeType,
    payee_id: payeeType === 'expense_category' ? null : payeeId,
    ...(payeeType === 'expense_category' ? { category, sub_category: subCategory } : {}),
  };
  const r = await api.post('/banking/bank-payments/bulk-from-stmt-txns', body);
  toast.success(`Linked ${r.data?.created_count} of ${group.count} lines`);
  if (createRule) {
    try {
      await api.post('/banking/rules', buildRulePayload({ group, payeeType, payeeId, category, subCategory, scopeRuleToBank }));
      toast.success(scopeRuleToBank
        ? 'Banking rule created (scoped to this bank) — future matching lines will auto-link'
        : 'Banking rule created — future matching lines will auto-link');
    } catch (e) {
      toast.error(`Lines linked, but rule creation failed: ${e?.response?.data?.detail || 'unknown error'}`);
    }
  }
}

async function submitPerRow({ group, rowAssignments, txnKey }) {
  // Group assignments by (payee_type, payee_id, category, sub_category) so we
  // fire one bulk call per distinct target — fewer round-trips than one-call-per-row.
  const buckets = {};
  for (const t of group.transactions) {
    const a = rowAssignments[txnKey(t)];
    if (!a || a.skip || !a.payee_type) continue;
    if (a.payee_type !== 'expense_category' && !a.payee_id) continue;
    const bk = JSON.stringify({
      pt: a.payee_type, pi: a.payee_id || '', c: a.category || '', sc: a.sub_category || '',
    });
    if (!buckets[bk]) buckets[bk] = { meta: a, refs: [] };
    buckets[bk].refs.push({ statement_id: t.statement_id, txn_index: t.txn_index });
  }
  const bucketList = Object.values(buckets);
  if (bucketList.length === 0) {
    // Throw so the outer onClick catch keeps the dialog open and the user
    // doesn't lose their partial assignments.
    throw new Error('No rows assigned — pick a target for at least one row, or use single-picker mode');
  }
  let totalCreated = 0;
  for (const b of bucketList) {
    const body = {
      txn_refs: b.refs,
      payee_type: b.meta.payee_type,
      payee_id: b.meta.payee_type === 'expense_category' ? null : b.meta.payee_id,
      ...(b.meta.payee_type === 'expense_category'
        ? { category: b.meta.category || 'Other', sub_category: b.meta.sub_category || '' }
        : {}),
    };
    const r = await api.post('/banking/bank-payments/bulk-from-stmt-txns', body);
    totalCreated += (r.data?.created_count || 0);
  }
  toast.success(`Linked ${totalCreated} rows across ${bucketList.length} target${bucketList.length === 1 ? '' : 's'}`);
}
